import { corporateWalletFlag } from '../config/build';

/**
 * Pure locale-tag helpers shared by the i18n bootstrap (`src/i18n.ts`) and the
 * language preference API (`src/utils/language.ts`).
 *
 * This module deliberately depends only on the dependency-free build flags in
 * `../config/build` (never on `i18n`, the Redux store or `src/utils.ts`), so it
 * can be imported during i18n initialisation without a circular import or heavy
 * load-time side effects. The stateful `setPreferredLanguage` (which calls
 * `i18n.changeLanguage`) lives in `src/utils/language.ts`, which re-exports
 * everything here.
 */

export type LanguageCode = 'en' | 'en-US';

/** localStorage key holding the user's chosen dialect. */
export const PREFERRED_LANGUAGE_KEY = 'preferredLanguage';

/**
 * Map any BCP-47 language tag to one of our supported dialects.
 * Separators are canonicalised (`en_US` → `en-us`) before matching, so
 * underscore- and hyphen-delimited tags resolve identically. Anything resolving
 * to American English becomes `en-US`; everything else (other English variants
 * and non-English locales) uses the British base `en`. Corporate builds are
 * single-bundle and always normalise to `en`.
 */
export const normaliseLanguage = (input?: string | null): LanguageCode => {
	if (corporateWalletFlag) return 'en';
	const tag = (input ?? '').toLowerCase().replace(/_/g, '-');
	return tag === 'en-us' || tag.startsWith('en-us-') ? 'en-US' : 'en';
};

/**
 * Resolve the initial language for the current session: an explicit saved
 * preference wins, otherwise fall back to the browser locale, then `en`.
 */
export const detectInitialLanguage = (): LanguageCode => {
	try {
		const saved = localStorage.getItem(PREFERRED_LANGUAGE_KEY);
		if (saved) return normaliseLanguage(saved);
	} catch {
		// localStorage unavailable (e.g. SSR / privacy mode) — fall through.
	}
	const browser = typeof navigator !== 'undefined' ? navigator.language : undefined;
	return normaliseLanguage(browser);
};
